import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  signal,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Store } from '@ngxs/store';
import {
  AddAddOn,
  AddCreditAppliedClientIds,
  AddPayLateFeeClientIds,
  CancelCurrentPayment,
  ClearCurrentPaymentActionRequirement,
  GetAddOnOptions,
  GetClients,
  GetCredits,
  GetJobs,
  GetLateFees,
  GetPayables,
  GetPaymentFeeConfigs,
  MakePayment,
  PaymentsState,
  RemoveAddOn,
  RemoveArTranAllocations,
  RemoveCreditAppliedClientIds,
  RemovePayLateFeeClientIds,
  SetAdditionalRecipientEmail,
  SetPayWithInvoices,
  SetProvidedPrimaryRecipientEmail,
  SubmitCreditAllocations,
  UpdatePayWithoutInvoicePayable,
} from '../../state';
import {
  ClientPaymentSummaryItemToggledEvent,
  PaymentActionRequirementDialogComponent,
  PaymentActionRequirementDialogData,
  PaymentSummaryComponent,
  PayWithoutInvoiceFormComponent,
  RecipientEmailsComponent,
} from '../../components';
import { CommonSettingsState, GetAutoPayClients } from '@aiwyn/common/settings';
import {
  AiwynBreakpoint,
  distinctUntilObjectChanged,
  extractFailureContext,
  getActionIfNeeded,
  getFirstOfQueryList,
  getLoadedResults,
  ignoreFalsy,
  ignoreNullish,
  ignoreTruthy,
  invert,
  isEmpty,
  LoadableStatus,
  mapToAndResult,
  removeDuplicates,
  SentenceCasePipe,
  TrackService,
} from '@aiwyn/shared';
import {
  CreditApplicationTrackService,
  CreditApplicationWorkflowEvent,
  PaymentCancellationSource,
  PaymentMethodSurface,
  PaymentTrackService,
  PaymentWorkflowEvent,
  THREE_DS_USER_CANCEL_ERROR_NAME,
} from '../../tracking';
import { AddOn } from '../../../../model';
import { PayableType } from '../../../../components/main/invoices/models/payable-table-object';
import { AbstractPaymentMethod, PaymentFlow, PaymentMethodIntent, PaymentMethodMode, PaymentPlatformService } from '../../platforms';
import {
  catchError,
  delay,
  distinctUntilChanged,
  filter,
  map,
  mapTo,
  pairwise,
  shareReplay,
  skip,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { BehaviorSubject, combineLatest, concat, Observable, of, Subject } from 'rxjs';
import { AuthAdaptorService, AuthStateAdaptorService, UserType } from '@aiwyn/auth';
import { PayWithoutInvoicePayable } from '../../models';
import { Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import { UntypedFormControl, Validators } from '@angular/forms';
import { BreakpointObserver } from '@angular/cdk/layout';
import { SelectPaymentMethodComponent } from '../select-payment-method/select-payment-method.component';
import { ConfigService, LaunchDarklyFlag, LaunchDarklyService } from '@aiwyn/config';
import { PaymentsSharedService } from '../../services';
import { MatRadioChange } from '@angular/material/radio';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-payments-sidebar',
  templateUrl: './payments-sidebar.component.html',
  styleUrls: ['./payments-sidebar.component.scss'],
  standalone: false,
})
export class PaymentsSidebarComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly paymentTrackService = inject(PaymentTrackService);
  private readonly creditApplicationTrackService = inject(CreditApplicationTrackService);
  // The "Client Applied Credit" event below is a deprecated legacy fire that doesn't fit the
  // workflow taxonomy — stays on the generic TrackService surface until dashboards retire it.
  private readonly legacyTrackService = inject(TrackService);

  @Output() openedChange = new EventEmitter<boolean>();

  readonly PaymentMethodIntent = PaymentMethodIntent;
  readonly PaymentFlow = PaymentFlow;
  readonly LoadableStatus = LoadableStatus;
  /**
   * URL-derived `PaymentMethodSurface` for the sidebar's `<app-select-payment-method>` host.
   * Read fresh on every change-detection pass — matches `PaymentsState.buildPaymentEventBase`,
   * which resolves the payment surface freshly each time the event base is built. This keeps the
   * payment-method funnel aligned to the user's *current* URL if they navigate between routes
   * while the sidebar stays open.
   */
  get paymentMethodSurface(): PaymentMethodSurface {
    return PaymentsState.resolvePaymentMethodSurfaceFromUrl();
  }
  private readonly _unsubscribe$ = new Subject<void>();

  @ViewChild('sidebarScrollContent') sidebarScrollContent: ElementRef<HTMLDivElement>;
  @ViewChild(
    PayWithoutInvoiceFormComponent,
    { read: ElementRef },
  ) payWithoutInvoiceComponent: ElementRef<PayWithoutInvoiceFormComponent & HTMLElement>;

  showFooterBoxShadow$ = new BehaviorSubject<boolean>(false);

  dispatchInitializationActions$ = this.authStateAdaptor.loginComplete().pipe(
    ignoreFalsy(),
    take(1),
    mapTo(this.getInitializationActionsToDispatch(this.authAdaptor.isAdmin)),
    filter(initializationActions => initializationActions.length > 0),
    switchMap(initializationActions => this.store.dispatch(initializationActions)),
    take(1),
    takeUntil(this._unsubscribe$),
  );

  paymentMethodMode$ = this.paymentPlatformService.getPaymentMethodMode();
  selectedPaymentMethod$ = this.paymentPlatformService.getSelectedPaymentMethod();
  selectedPaymentMethodId$ = this.selectedPaymentMethod$.pipe(
    map(paymentMethod => paymentMethod?.id),
    distinctUntilChanged(),
    delay(1), // ugly ExpressionChangedAfterItHasBeenCheckedError workaround
  );

  doAppliedCreditsCoverArTranAllocations$ = combineLatest([
    this.store.select(PaymentsState.getPayWithInvoices),
    this.store.select(PaymentsState.doAppliedCreditsCoverArTranAllocations),
  ]).pipe(
    mapToAndResult(),
    shareReplay(1),
  );

  activeCurrency$ = this.store.select(PaymentsState.getActiveCurrency);
  isActiveCurrencyUsd$ = this.activeCurrency$.pipe(
    map(currency => !currency || currency?.code === 'USD'),
  );

  isPayLateFeesByClientEnabled$ = this.launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_APPLY_CREDITS_TO_LATE_FEES);
  areLateFeesByClient$ = this.isPayLateFeesByClientEnabled$.pipe(
    switchMap(payLateFeesByClientEnabled => payLateFeesByClientEnabled ? this.store.select(PaymentsState.getPayWithInvoices) : of(false)),
  );
  shouldIgnoreLateFees$ = this.areLateFeesByClient$.pipe(
    switchMap(areLateFeesByClient => areLateFeesByClient ? this.store.select(PaymentsState.getCanPayLateFees) : of(true)),
    invert(),
  );
  disableLateFeesMessage$ = this.isPayLateFeesByClientEnabled$.pipe(
    ignoreTruthy(),
    switchMap(_ => combineLatest([
      this.doAppliedCreditsCoverArTranAllocations$,
      this.isActiveCurrencyUsd$,
    ])),
    map(([doCreditsCoverArTranAllocations, isActiveCurrencyUsd]) => {
      const lateFeeDisplay = SentenceCasePipe.staticTransform(this.configService.lateFeeDisplayName);
      if (!isActiveCurrencyUsd) {
        return `${lateFeeDisplay}s cannot be paid when the selected invoices are not in USD.`;
      } else if (doCreditsCoverArTranAllocations) {
        return `${lateFeeDisplay}s cannot be paid when credits fully cover the balance of selected invoices.`;
      }

      return undefined;
    }),
  );
  lateFeeTooltipMessage$ = this.isPayLateFeesByClientEnabled$.pipe(
    ignoreFalsy(),
    switchMap(_ => this.store.select(PaymentsState.getPayWithInvoices)),
    map(payWithInvoices => {
      if (payWithInvoices) {
        return `This is the sum of the ${this.configService.paymentLateFeeRequired ? '' : 'selected'} `
          + `${this.configService.lateFeeDisplayName}s shown above.`;
      }

      return `This is the sum of all ${this.configService.lateFeeDisplayName}s.`;
    }),
  );

  doAppliedCreditsCoverSubtotal$ = combineLatest([
    this.store.select(PaymentsState.getPayWithInvoices),
    this.store.select(PaymentsState.doAppliedCreditsCoverSubtotal),
  ]).pipe(
    mapToAndResult(),
    shareReplay(1),
  );
  // when applied credits cover entire balance, payment method is not required
  isPaymentMethodRequired$ = this.doAppliedCreditsCoverSubtotal$.pipe(
    invert(),
  );

  // Email-first checkout kill switch + gate state. `emailValid` mirrors recipient-emails' (isValid) output;
  // the payment methods stay locked until it is true. `_methodsUnlockedTracked` keeps the unlock funnel
  // event to a single fire per session.
  isRequireEmailBeforePaymentEnabled$ = this.launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_REQUIRE_EMAIL_ADDRESS_BEFORE_PAYMENT_METHOD);
  emailValid = signal(false);
  // Mirrors recipient-emails' (primaryEmailPrepopulated); hides the gate status banners when true.
  emailGatePrepopulated = signal(false);
  private _methodsUnlockedTracked = false;

  total$ = combineLatest([
    this.selectedPaymentMethod$,
    this.doAppliedCreditsCoverArTranAllocations$,
    this.activeCurrency$,
  ]).pipe(
    switchMap(([selectedPaymentMethod, doCreditsCoverArTranAllocations, activeCurrency]) =>
      this.store.select(PaymentsState.getTotal(
        selectedPaymentMethod,
        { ignoreLateFees: doCreditsCoverArTranAllocations, currencyCode: activeCurrency.code },
      )),
    ),
    delay(1), // ugly ExpressionChangedAfterItHasBeenCheckedError workaround
  );

  payablesExist$ = getLoadedResults(this.store.select(PaymentsState.getRawPayablesLoadable)).pipe(
    map(payables => !isEmpty(payables)),
  );
  payWithInvoices$ = this.store.select(PaymentsState.getPayWithInvoices);
  setPayWithoutInvoicesOnNoPayables$ = getLoadedResults(this.store.select(PaymentsState.getPayablesLoadable)).pipe(
    take(1),
    filter(payables => isEmpty(payables)),
    tap(_ => this.store.dispatch(new SetPayWithInvoices(false))),
  );

  clientPaymentSummaries$ = this.store.select(PaymentsState.getClientPaymentSummaries);
  trackByClientId = (_: number, { client }) => client.id;

  selectedPayablesCount$ = this.store.select(PaymentsState.getSelectedPayableIds).pipe(
    map(({ length }) => length),
  );
  addOnOptions$ = this.store.select(PaymentsState.getAddOnOptionsLoadable);

  setPayWithInvoicesBySelectedPayables$ = combineLatest([
    this.store.select(PaymentsState.getSelectedPayableIds),
    this.store.select(PaymentsState.getPayWithInvoices),
  ]).pipe(
    pairwise(),
    filter(([[previousSelected, previousPay], [currentSelected, currentPay]]) => {
      // ignore if payWithInvoices is already set to true or the user manually toggled it
      if (currentPay || currentPay !== previousPay) {
        return false;
      }

      /**
       * If the user selects any payables while payWithInvoices is false, set payWithInvoices to true.
       *
       * Originally, the logic was to check if the current selection is not empty and does not equal the previous selection (first by
       * comparing length then selected IDs), but this did not account for when the user selects payable A, clicks pay without invoice,
       * then selects payable A again. To work around this, whenever a selection is made when pay without invoice is active, the existing
       * selections are first cleared then populated with the new selected payable.
       */
      return previousSelected.length === 0 && currentSelected.length > 0;
    }),
    tap(_ => this.store.dispatch(new SetPayWithInvoices(true))),
    takeUntil(this._unsubscribe$),
  );

  isSelectedPayablesValid$ = new BehaviorSubject<boolean>(true);

  //#region pay without invoice
  payWithoutInvoicePayable$ = this.store.select(PaymentsState.getPayWithoutInvoicePayable).pipe(
    delay(1), // ugly ExpressionChangedAfterItHasBeenCheckedError workaround
  );
  jobsLoadable$ = this.store.select(PaymentsState.getJobsLoadable);
  clientsLoadable$ = this.store.select(PaymentsState.getClientsLoadable);

  onPayWithoutInvoicePayableChange(partialPayWithoutInvoicePayable: Partial<PayWithoutInvoicePayable>): void {
    this.store.dispatch(new UpdatePayWithoutInvoicePayable(partialPayWithoutInvoicePayable));
    // PP-3775: "or just start editing it at all" — editing the free-form payable is also an intent-based
    // start. The latch in _fireNonInvoiceStartedOnce dedupes this against the toggle above.
    this._fireNonInvoiceStartedOnce();
  }
  //#endregion

  today = new DatePipe('en-US').transform(new Date().toDateString(), 'mediumDate');

  primaryRecipientEmail$ = this.paymentsState.getPrimaryRecipientEmail();

  // If guest user, ACSS is enabled, primary recipient email input is not yet populated, and ACSS/ACH was created, use the email from it.
  setPrimaryEmailWhenApplicable$ = this.authStateAdaptor.getUserType().pipe(
    ignoreNullish(),
    take(1),
    filter(userType => userType === UserType.GUEST),
    switchMap(_ => this.paymentPlatformService.getTemporaryPaymentMethod()),
    map(temporaryPaymentMethod => temporaryPaymentMethod?.billingDetails?.email),
    ignoreNullish(),
    take(1),
    tap(paymentMethodEmail => {
      const existingPrimaryEmail = this.paymentsState.getPrimaryRecipientEmailSnapshot();
      if (existingPrimaryEmail == undefined) {
        this.store.dispatch(new SetProvidedPrimaryRecipientEmail(paymentMethodEmail));
      }
    }),
    takeUntil(this._unsubscribe$),
  );

  additionalRecipientEmails$ = this.store.select(PaymentsState.getAdditionalRecipientEmails);

  termsCheckboxControl = new UntypedFormControl(false, this.authAdaptor.isContactOrAdmin ? [] : Validators.requiredTrue);

  isSidebarPopover$: Observable<boolean> = this.breakpointObserver.observe([AiwynBreakpoint.SIDEBAR_POPOVER]).pipe(
    map(breakpointMatched => breakpointMatched.matches),
  );

  isReviewAndPayPage = this.router.url.includes('reviewAndPay');

  /**
   * Occasionally, it seems authentication issues are causing the payment platform service to fail initialization.
   * 9/10 times, this is caused by missing Hasura claims in the JWT.
   *
   * Also disable when an AR transaction allocation is $0 or negative - the partial-payment input only flags invalid
   * entries visually; without blocking here, a zero allocation would slip through to the charge payload (PP-3093).
   * Gate the allocation check behind `payWithInvoices` to mirror `validate()` — stale allocations left in state
   * after toggling to non-invoice mode must not disable Pay.
   */
  shouldDisablePay$ = this.authStateAdaptor.loginComplete().pipe(
    ignoreFalsy(),
    switchMap(_ => combineLatest([
      this.store.select(PaymentsState.getActivePlatform).pipe(ignoreNullish(), invert()),
      this.store.select(PaymentsState.getPayWithInvoices).pipe(
        switchMap(payWithInvoices =>
          payWithInvoices
            ? this.store.select(PaymentsState.hasInvalidArTranAllocations)
            : of(false),
        ),
      ),
    ])),
    map(([isPlatformMissing, hasInvalidAllocations]) => isPlatformMissing || hasInvalidAllocations),
    startWith(true),
  );

  /**
   * Payment platform's selected business entity can be set by data other than AR Tran Allocations.
   * Ensure we pivot back to allocations as the source of truth.
   */
  alignInitialSelectedBusinessEntity$ = getLoadedResults(this.store.select(PaymentsState.getRawPayablesLoadable)).pipe(
    switchMap(_ => combineLatest([
      this.store.select(PaymentsState.getSelectedBusinessEntity(true)),
      this.paymentPlatformService.getSelectedBusinessEntityId(),
    ])),
    take(1),
    filter(([selectedBusinessEntity, selectedBusinessEntityId]) =>
      selectedBusinessEntity && selectedBusinessEntity.id !== selectedBusinessEntityId,
    ),
    switchMap(([selectedBusinessEntity]) => this.paymentPlatformService.setSelectedBusinessEntityId(selectedBusinessEntity.id)),
  );

  alignSelectedBusinessEntity$ = this.store.select(PaymentsState.getSelectedBusinessEntity(true)).pipe(
    ignoreNullish(),
    map(({ id }) => id),
    distinctUntilChanged(),
    switchMap(businessEntityId => this.paymentPlatformService.setSelectedBusinessEntityId(businessEntityId)),
    takeUntil(this._unsubscribe$),
  );

  constructor(
    private readonly store: Store,
    private readonly launchDarklyService: LaunchDarklyService,
    private readonly paymentsSharedService: PaymentsSharedService,
    readonly authAdaptor: AuthAdaptorService,
    readonly configService: ConfigService,
    private readonly paymentPlatformService: PaymentPlatformService,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly host: ElementRef<PaymentsSidebarComponent & HTMLElement>,
    private readonly authStateAdaptor: AuthStateAdaptorService,
    private readonly paymentsState: PaymentsState,
  ) {
    this.dispatchInitializationActions$.subscribe();
    this.setPayWithInvoicesBySelectedPayables$.subscribe();
    this.setPayWithoutInvoicesOnNoPayables$.subscribe();
    this.setPrimaryEmailWhenApplicable$.subscribe();
    this.handlePaymentActionRequirements$.subscribe();

    if (this.configService.setupIntentByBusinessEntity) {
      concat(this.alignInitialSelectedBusinessEntity$, this.alignSelectedBusinessEntity$).subscribe();
    }
  }

  getInitializationActionsToDispatch(isAdmin: boolean): unknown[] {
    return []
      .concat(getActionIfNeeded(this.store, CommonSettingsState.getAutoPayClientsStatus, GetAutoPayClients))
      .concat(getActionIfNeeded(this.store, PaymentsState.getRawPayablesStatus, GetPayables))
      .concat(getActionIfNeeded(this.store, PaymentsState.getCreditsStatus, GetCredits))
      .concat(getActionIfNeeded(this.store, PaymentsState.getAddOnOptionsStatus, GetAddOnOptions))
      .concat(getActionIfNeeded(this.store, PaymentsState.getLateFeesStatus, GetLateFees))
      .concat(getActionIfNeeded(this.store, PaymentsState.getClientsStatus, GetClients))
      .concat(getActionIfNeeded(this.store, PaymentsState.getPaymentFeeConfigsStatus, GetPaymentFeeConfigs))
      .concat(isAdmin ? getActionIfNeeded(this.store, PaymentsState.getJobsStatus, GetJobs) : []);
  }

  ngOnInit(): void {
    // Funnel entry for the make-a-payment flow. Fires on every page entry that mounts the
    // sidebar (/invoices, /review-and-pay), before any child SelectPaymentMethodComponent or
    // payable-selection child can emit — guarantees SETUP_STARTED precedes all other
    // PaymentWorkflowEvents within a given page visit.
    this.paymentTrackService.trackWorkflow(
      PaymentWorkflowEvent.PAYMENT_SETUP_STARTED,
      PaymentsState.buildPaymentEventBase(this.store),
    );
  }

  ngAfterViewInit(): void {
    new ResizeObserver(([sidebarScrollContentElement]) => {
      const { scrollHeight, offsetHeight } = sidebarScrollContentElement.target as HTMLDivElement;
      this.showFooterBoxShadow$.next(scrollHeight > offsetHeight);
    }).observe(this.sidebarScrollContent.nativeElement);
  }

  // PP-3775: latches PAYMENT_NON_INVOICE_STARTED so toggling to non-invoice and then editing the amount
  // only fires the funnel start once. Reset when the user toggles back to invoices so a later re-entry
  // into the non-invoice flow fires again.
  private _hasFiredNonInvoiceStarted = false;

  setPayWithInvoices({ value: payWithInvoices }: MatRadioChange): void {
    this.store.dispatch(new SetPayWithInvoices(payWithInvoices));

    // PP-3775: the user toggling to "Pay without invoice" is an intent-based start for the non-invoice
    // funnel — fired here in the user-driven radio handler, not in the SetPayWithInvoices action, so the
    // surface-driven auto-toggles (open-portal / quick-payment-link) don't count as a manual start.
    if (payWithInvoices) {
      this._hasFiredNonInvoiceStarted = false;
    } else {
      this._fireNonInvoiceStartedOnce();
    }
  }

  private _fireNonInvoiceStartedOnce(): void {
    if (this._hasFiredNonInvoiceStarted) {
      return;
    }
    this._hasFiredNonInvoiceStarted = true;
    this.paymentTrackService.trackWorkflow(
      PaymentWorkflowEvent.PAYMENT_NON_INVOICE_STARTED,
      PaymentsState.buildPaymentEventBase(this.store),
    );
  }

  removeSelectedClientInvoices(clientId: number): void {
    this._removeSelectedClientPayablesByType(clientId, type => type !== PayableType.PREPAYMENT);
  }

  removeSelectedClientPrepayments(clientId: number): void {
    this._removeSelectedClientPayablesByType(clientId, type => type === PayableType.PREPAYMENT);
  }

  private _removeSelectedClientPayablesByType(clientId: number, matchesType: (type: PayableType) => boolean): void {
    const selectedClientPayables = this.store.selectSnapshot(PaymentsState.getSelectedClientPayables(clientId));
    const selectedClientPayableIds = selectedClientPayables
      .filter(payable => matchesType(payable.getType()))
      .map(({ arTranId }) => arTranId);
    this.store.dispatch(new RemoveArTranAllocations(selectedClientPayableIds));
  }

  payClientLateFees({ clientId, isOn }: ClientPaymentSummaryItemToggledEvent): void {
    this.store.dispatch(new (isOn ? AddPayLateFeeClientIds : RemovePayLateFeeClientIds)(clientId));
  }

  applyCredits({ clientId, isOn }: ClientPaymentSummaryItemToggledEvent): void {
    // Track the start of the credit-application workflow on the first toggle ON of the session.
    // Subsequent toggles re-fire INVOICES_SELECTED with the latest allocation snapshot.
    if (isOn) {
      const previouslyAppliedClientIds = this.store.selectSnapshot(PaymentsState.getCreditAppliedClientIds) ?? [];
      if (previouslyAppliedClientIds.length === 0) {
        this.creditApplicationTrackService.trackWorkflow(CreditApplicationWorkflowEvent.CREDIT_APPLICATION_STARTED);
      }
    }

    this.store.dispatch(new (isOn ? AddCreditAppliedClientIds : RemoveCreditAppliedClientIds)(clientId));

    if (isOn) {
      const creditAllocations = this.store.selectSnapshot(PaymentsState.getCreditAllocations) ?? [];
      const totalAmount = Math.abs(this.store.selectSnapshot(PaymentsState.getAppliedCreditTotal) ?? 0);
      const invoiceCount = removeDuplicates(creditAllocations.map(({ arTranId }) => arTranId)).length;
      this.creditApplicationTrackService.trackWorkflow(
        CreditApplicationWorkflowEvent.CREDIT_APPLICATION_INVOICES_SELECTED,
        { invoiceCount, totalAmount },
      );
    }
  }

  addAddOn(addOn: AddOn): void {
    this.store.dispatch(new AddAddOn(addOn));
  }

  removeAddOn(addOnId: number): void {
    this.store.dispatch(new RemoveAddOn(addOnId));
  }

  onPaymentMethodModeChange(paymentMethodMode: PaymentMethodMode): void {
    this.paymentPlatformService.setPaymentMethodMode(paymentMethodMode).pipe(take(1)).subscribe();
  }

  onPaymentMethodSelected(paymentMethod: AbstractPaymentMethod): void {
    // paymentMethod can emit as undefined (eg. when the user has no payment methods or is adding a new payment method)
    this.paymentPlatformService.setSelectedSavedPaymentMethodId(paymentMethod?.id).pipe(take(1)).subscribe();
  }

  onPrimaryEmailChanged(primaryEmail: string): void {
    this.store.dispatch(new SetProvidedPrimaryRecipientEmail(primaryEmail));
  }

  // Recipient-emails reports overall email validity; drive the method gate from it and fire the unlock
  // funnel event once, the first time a valid email unlocks the methods.
  onEmailValidChanged(isValid: boolean): void {
    this.emailValid.set(isValid);

    if (isValid && !this._methodsUnlockedTracked) {
      this._methodsUnlockedTracked = true;
      this.paymentTrackService.trackWorkflow(
        PaymentWorkflowEvent.PAYMENT_METHODS_UNLOCKED,
        PaymentsState.buildPaymentEventBase(this.store),
      );
    }
  }

  // Hide the gate status banners for a prepopulated, untouched email.
  onPrimaryEmailPrepopulated(prepopulated: boolean): void {
    this.emailGatePrepopulated.set(prepopulated);
  }

  // A payer clicked the locked methods before entering a valid email — surface the email error and move
  // focus to the email field.
  onLockedPaymentMethodClick(): void {
    this.recipientEmailsComponent?.primaryEmailControl.markAsTouched();
    this.recipientEmailsComponent?.focusPrimaryEmail();
  }

  onAdditionalEmailsChanged(additionalEmails: string[]): void {
    this.store.dispatch(new SetAdditionalRecipientEmail(additionalEmails));
  }

  handlePaymentActionRequirements$ = this.store.select(PaymentsState.getCurrentPaymentActionRequirement).pipe(
    /**
     * Wait until a valid payment action requirement is emitted.
     */
    ignoreNullish(),
    filter(paymentActionRequirement => !paymentActionRequirement.cancelled),
    distinctUntilObjectChanged(),

    /**
     * Open a dialog to inform the user of the required action and prompt them to continue. Stripe handles the rest of the process.
     */
    switchMap(paymentActionRequirement => {
      // if other actions are required, paymentActionRequirement will be used to determine dialog data
      const dialog = this.dialog.open<PaymentActionRequirementDialogComponent, PaymentActionRequirementDialogData, boolean>(
        PaymentActionRequirementDialogComponent,
        { data: { header: 'This card requires 3D Secure Authentication' } },
      );

      return dialog.afterClosed().pipe(
        switchMap(shouldContinue => {
          if (!shouldContinue) {
            return of<Error>({ name: THREE_DS_USER_CANCEL_ERROR_NAME, message: 'The payment was canceled.' });
          }

          // user agreed to continue with 3D Secure Authentication
          return this.paymentPlatformService.getSelectedPaymentMethod().pipe(
            take(1),
            switchMap(selectedPaymentMethod => {
              const eventBase = PaymentsState.buildPaymentEventBase(this.store, selectedPaymentMethod);
              this.paymentTrackService.trackWorkflow(PaymentWorkflowEvent.PAYMENT_PLATFORM_CONFIRMATION_SUBMITTED, eventBase);
              const confirmationKey = `PAYMENT_PLATFORM_CONFIRMATION:${eventBase.paymentBatchId ?? 'no-batch'}`;
              this.paymentTrackService.startOperation(confirmationKey);

              return this.paymentPlatformService.handlePaymentActionRequirement(paymentActionRequirement, selectedPaymentMethod.type).pipe(
                tap(actionError => {
                  if (actionError == undefined) {
                    this.paymentTrackService.trackWorkflow(PaymentWorkflowEvent.PAYMENT_PLATFORM_CONFIRMATION_COMPLETE, {
                      ...eventBase,
                      operationDurationMs: this.paymentTrackService.endOperation(confirmationKey),
                    });
                  } else {
                    this.paymentTrackService.trackWorkflow(
                      PaymentWorkflowEvent.PAYMENT_PLATFORM_CONFIRMATION_FAILED,
                      {
                        ...eventBase,
                        ...extractFailureContext(actionError),
                        operationDurationMs: this.paymentTrackService.endOperation(confirmationKey),
                      },
                    );
                  }
                }),
              );
            }),
          );
        }),
      );
    }),

    /**
     * React to user's decision. If the user cancels the required action, cancel the payment, which, in turn, cancels the entire batch.
     * Otherwise, clear the current payment action requirement.
     */
    switchMap(actionError => {
      if (actionError == undefined) {
        return this.store.dispatch(new ClearCurrentPaymentActionRequirement());
      }
      // The dialog-dismissed path produces the synthetic THREE_DS_USER_CANCEL_ERROR_NAME error above;
      // everything else is an actionError surfaced by Stripe's confirm*Payment.
      const cancellationSource: PaymentCancellationSource = actionError.name === THREE_DS_USER_CANCEL_ERROR_NAME
        ? PaymentCancellationSource.THREE_DS_USER_CANCEL
        : PaymentCancellationSource.THREE_DS_AUTH_FAILED;
      return this.store.dispatch(new CancelCurrentPayment(actionError, cancellationSource));
    }),
    takeUntil(this._unsubscribe$),
  );

  isPaymentProcessing$ = new BehaviorSubject<boolean>(false);
  onPayNow(): void {
    this.isPaymentProcessing$.next(true);

    const appliedCreditTotal = this.store.selectSnapshot(PaymentsState.getAppliedCreditTotal);
    if (Math.abs(appliedCreditTotal) > 0) {
      this.legacyTrackService.track('Client Applied Credit');
    }

    this.validate().pipe(
      /**
       * Validate the form and begin the payment process.
       */
      take(1),
      tap(isValid => {
        const isPayWithInvoice = this.store.selectSnapshot(PaymentsState.getPayWithInvoices);

        if (!isValid) {
          this.paymentTrackService.trackWorkflow(
            PaymentWorkflowEvent.PAYMENT_VALIDATION_FAILED,
            {
              ...PaymentsState.buildPaymentEventBase(this.store),
              errorCategory: 'CLIENT_ERROR',
              errorMessage: 'Form validation failed',
            },
          );
        }

        if (!isValid && !isPayWithInvoice) {
          this.payWithoutInvoiceComponent?.nativeElement?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          });
        }

        this.isPaymentProcessing$.next(isValid);
      }),
      ignoreFalsy(),
      switchMap(_ => this.selectedPaymentMethod$.pipe(take(1))),
      switchMap(_ => this.doAppliedCreditsCoverSubtotal$),
      take(1),
      switchMap(doAppliedCreditsCoverEntireBalance =>
        this.store.dispatch(new (doAppliedCreditsCoverEntireBalance ? SubmitCreditAllocations : MakePayment)()),
      ),

      /**
       * React to payment(s) result.
       */
      catchError(_ => of(undefined)),
      switchMap(_ => {
        this.host.nativeElement.firstElementChild.scrollTo({ top: 0, behavior: 'smooth' });
        this.isPaymentProcessing$.next(false);
        return this.paymentsSharedService.onPaymentCompletion();
      }),

      /**
       * Stop processing if any payment within the current batch was cancelled.
       */
      takeUntil(
        this.store.select(PaymentsState.isLastExecutedPaymentBatchCancelled).pipe(
          skip(1), // skip the initial value (emits before current payment batch begins)
          distinctUntilChanged(),
          ignoreFalsy(),
          tap(_ => this.isPaymentProcessing$.next(false)),
        ),
      ),
    ).subscribe();
  }

  @ViewChildren(PayWithoutInvoiceFormComponent) payWithoutInvoiceFormComponent: QueryList<PayWithoutInvoiceFormComponent>;
  @ViewChild(SelectPaymentMethodComponent) selectPaymentMethodComponent: SelectPaymentMethodComponent;
  @ViewChild(RecipientEmailsComponent) recipientEmailsComponent: RecipientEmailsComponent;
  @ViewChild(PaymentSummaryComponent) paymentSummaryComponent: PaymentSummaryComponent;
  validate(): Observable<boolean> {
    if (!this.authAdaptor.isContactOrAdmin) {
      this.termsCheckboxControl.markAsTouched();
    }

    const payWithInvoices = this.store.selectSnapshot(PaymentsState.getPayWithInvoices);
    return combineLatest([
      payWithInvoices
        ? this.selectedPayablesCount$.pipe(
          map(selectedPayablesCount => selectedPayablesCount > 0),
          tap(isSelectedPayablesValid => this.isSelectedPayablesValid$.next(isSelectedPayablesValid)),
        )
        : getFirstOfQueryList(this.payWithoutInvoiceFormComponent).pipe(
          switchMap(payWithoutInvoiceFormComponent => payWithoutInvoiceFormComponent.validate()),
        ),
      payWithInvoices
        ? this.store.select(PaymentsState.hasInvalidArTranAllocations).pipe(invert())
        : of(true),
      this.isPaymentMethodRequired$.pipe(
        switchMap(paymentMethodRequired => paymentMethodRequired ? this.selectPaymentMethodComponent.validate() : of(true)),
      ),
      this.recipientEmailsComponent.validate(),
      this.paymentSummaryComponent.validate(),
      of(this.termsCheckboxControl.valid),
    ]).pipe(
      take(1),
      map(validities => !validities.includes(false)),
    );
  }

  onReviewInvoices(): void {
    const selectedPayableArTranIds = this.store.selectSnapshot(PaymentsState.getSelectedPayableIds);
    const payWithInvoices = this.store.selectSnapshot(PaymentsState.getPayWithInvoices);
    if (selectedPayableArTranIds.length === 0 || !payWithInvoices) {
      return;
    }

    // PP-3775: the entry source for this in-portal path is resolved by ReviewAndPayComponent on load — it
    // navigates here without the pay-invoice-email navigation state, so the component records
    // surface: 'review-and-pay'. No dispatch is needed here.
    const payableIdsQueryParam = this.store.selectSnapshot(PaymentsState.getSelectedPayableIds).join(',');

    this.router.navigate(
      ['main/reviewAndPay'],
      { queryParams: { invoiceId: payableIdsQueryParam } },
    );
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }
}
